import { Anchor, Badge, Button, Group, Stack, Text } from '@mantine/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { rawApi } from '../../api/client';
import { listKey } from '../../api/queryKeys';
import { EmptyNote } from '../../components/RecordScreen';
import { enumColor, enumLabel } from '../../lib/enums';
import { formatDate, formatMoney, formatRelative } from '../../lib/format';
import { OrderDetailModal } from './OrderDetail';
import { ORDERS_PATH } from './orders';
import { PlaceOrderForm } from './PlaceOrderForm';

interface OrderRow {
  id: string;
  supplierId: string;
  status: string;
  orderedAt?: string | null;
  note?: string | null;
  lineCount: number;
  totalMinor: number;
  currency?: string | null;
}

/**
 * What to call an order nobody named.
 *
 * The fallback used to be "An order with nothing written against it", which is about the note and
 * reads as being about the goods — on a row that showed neither, an order of 46 kg of limes
 * announced itself as empty. The day it was sent is a name a manager can actually use, because it
 * is how they refer to it on the phone.
 */
function orderName(order: OrderRow): string {
  if (order.note) return order.note;
  return order.orderedAt ? `Order of ${formatDate(order.orderedAt)}` : 'Order not sent yet';
}

/**
 * This vendor's orders, on this vendor's page.
 *
 * Ordering from Mainland Fruit Co used to mean leaving Mainland Fruit Co: back to the supplier
 * list, over to the Orders tab, Record an order, and pick Mainland Fruit Co out of a dropdown —
 * having just been on their page, which already lists what you buy from them and what they charge
 * for it. The order form is opened from here with the vendor already known and their price list as
 * the thing you put quantities against.
 */
export function SupplierOrdersSection({
  supplierId,
  supplierName,
  locationId,
}: {
  supplierId: string;
  supplierName: string;
  locationId?: string;
}) {
  const queryClient = useQueryClient();
  const [opening, setOpening] = useState<string | null>(null);

  const orders = useQuery({
    queryKey: listKey(ORDERS_PATH, { supplierId }),
    queryFn: () => rawApi<OrderRow[]>('GET', `${ORDERS_PATH}?supplierId=${supplierId}`),
    enabled: Boolean(supplierId),
  });

  const rows = orders.data ?? [];
  // What is still coming first: an order nobody has delivered is the one worth chasing, and the
  // received ones are history that can sit underneath it.
  const open = rows.filter((o) => o.status === 'sent' || o.status === 'partiallyReceived');
  const rest = rows.filter((o) => !open.includes(o));

  return (
    <>
      {orders.isLoading ? (
        <EmptyNote>Loading…</EmptyNote>
      ) : orders.isError ? (
        // Never the empty state: "no orders" over an outage is how the same order gets placed twice.
        <EmptyNote>
          Their orders could not be loaded. Nothing has been lost — try again in a moment.
        </EmptyNote>
      ) : rows.length === 0 ? (
        <EmptyNote>Nothing ordered from {supplierName} yet. Order from them and it shows here.</EmptyNote>
      ) : (
        <Stack gap={4}>
          {[...open, ...rest].map((order) => (
            <Group key={order.id} gap="md" wrap="nowrap" align="baseline">
              <div style={{ flex: 1, minWidth: 0 }}>
                <Anchor
                  component="button"
                  type="button"
                  size="sm"
                  fw={600}
                  ta="left"
                  onClick={() => setOpening(order.id)}
                >
                  {orderName(order)}
                </Anchor>
                {/* What is actually on it. Without this the row carried a name, a date and a
                    status — nothing a manager could act on without opening every order in turn. */}
                <Text size="xs" c="dimmed">
                  {order.lineCount === 0
                    ? 'Nothing on it yet'
                    : `${order.lineCount === 1 ? '1 item' : `${order.lineCount} items`}${
                        order.currency ? ` · ${formatMoney(order.totalMinor, order.currency)}` : ''
                      }`}
                </Text>
              </div>
              <Text size="sm" c="dimmed" style={{ width: 140 }}>
                {order.orderedAt ? `sent ${formatRelative(order.orderedAt)}` : 'not sent yet'}
              </Text>
              <Badge variant="light" color={enumColor('purchaseOrderStatus', order.status) ?? 'gray'}>
                {enumLabel('purchaseOrderStatus', order.status)}
              </Badge>
            </Group>
          ))}
        </Stack>
      )}

      {opening && (
        <OrderDetailModal
          orderId={opening}
          locationId={locationId}
          onClose={() => setOpening(null)}
          onChanged={() => {
            void queryClient.invalidateQueries({ queryKey: listKey(ORDERS_PATH) });
          }}
        />
      )}
    </>
  );
}

/**
 * The button that starts an order from the vendor you are looking at.
 *
 * Beside the section heading rather than inside the list, so it is there on the day a vendor has
 * no orders at all — which is the day somebody most needs to place one.
 */
export function OrderFromThemButton({ supplierId, locationId }: { supplierId: string; locationId?: string }) {
  const queryClient = useQueryClient();
  const [placing, setPlacing] = useState(false);
  return (
    <>
      <Button size="compact-sm" onClick={() => setPlacing(true)}>
        Order from them
      </Button>
      {placing && (
        <PlaceOrderForm
          locationId={locationId}
          supplierId={supplierId}
          onClose={() => setPlacing(false)}
          onSaved={() => {
            void queryClient.invalidateQueries({ queryKey: listKey(ORDERS_PATH) });
          }}
        />
      )}
    </>
  );
}
